import app from 'flarum/forum/app';
import ComposePollPage from '../components/ComposePollPage';
import PollsPage from '../components/PollsPage';
import PollViewPage from '../components/PollViewPage';
import PollListState from '../states/PollListState';
import ItemList from 'flarum/common/utils/ItemList';
import Separator from 'flarum/common/components/Separator';
import Button from 'flarum/common/components/Button';
import SchedulePollModal from '../components/SchedulePollModal';

/**
 * The `UserControls` utility constructs a list of buttons for a user which
 * perform actions on it.
 */
export default {
  /**
   * Get a list of controls for a user.
   */
  controls(poll, context) {
    const items = new ItemList();
    const sections = ['poll', 'moderation', 'destructive'];
    sections.forEach(section => {
      const controls = this["".concat(section, "Controls")](poll, context).toArray();
      if (controls.length) {
        controls.forEach(item => items.add(item.itemName, item));
        items.add(section + 'Separator', m(Separator, null));
      }
    });
    return items;
  },
  /**
   * Get controls for direct modifcation actions on polls (e.g. vote, view voters).
   */
  pollControls(poll, context) {
    const items = new ItemList();
    return items;
  },
  /**
   * Get controls for a user pertaining to moderation (e.g. suspend, edit).
   */
  moderationControls(poll, context) {
    const items = new ItemList();
    if (poll.canEdit()) {
      items.add('edit', m(Button, {
        icon: "fas fa-pencil-alt",
        onclick: this.editAction.bind(this, poll)
      }, app.translator.trans("fof-polls.forum.poll_controls.edit_label")));
    }
    if (poll.canPublish() && poll.isDraft()) {
      var _poll$isScheduled;
      items.add('publish', m(Button, {
        icon: "fas fa-paper-plane",
        onclick: () => this.publishAction(poll)
      }, app.translator.trans('fof-polls.forum.poll_controls.publish_label')));
      items.add('schedulePublish', m(Button, {
        icon: "fas fa-clock",
        onclick: () => app.modal.show(SchedulePollModal, {
          poll,
          form: null,
          onSuccess: app.current.matches(PollsPage) ? () => m.redraw() : () => m.route.set(app.route('fof.polls.view', {
            id: poll.id()
          }))
        })
      }, app.translator.trans((_poll$isScheduled = poll.isScheduled) != null && _poll$isScheduled.call(poll) ? 'fof-polls.forum.poll_controls.edit_schedule_publish_label' : 'fof-polls.forum.poll_controls.schedule_publish_label')));
      if (poll.isScheduled()) {
        items.add('cancelSchedule', m(Button, {
          icon: "fas fa-times",
          onclick: () => this.cancelScheduleAction(poll)
        }, app.translator.trans('fof-polls.forum.poll_controls.cancel_schedule_label')));
      }
    }
    return items;
  },
  /**
   * Get controls for a user which are destructive (e.g. delete).
   * @protected
   */
  destructiveControls(poll, context) {
    const items = new ItemList();
    if (poll.canUnpublish()) {
      items.add('unpublish', m(Button, {
        icon: "fas fa-undo",
        onclick: () => this.unpublishAction(poll)
      }, app.translator.trans('fof-polls.forum.poll_controls.unpublish_label')));
    }
    if (poll.canDelete()) {
      items.add('delete', m(Button, {
        icon: "far fa-trash-alt",
        onclick: this.deleteAction.bind(this, poll)
      }, app.translator.trans("fof-polls.forum.poll_controls.delete_label")));
    }
    return items;
  },
  /**
   * Delete the poll.
   */
  async deleteAction(poll) {
    if (!confirm(app.translator.trans("fof-polls.forum.poll_controls.delete_confirmation"))) {
      return;
    }
    return poll.delete().then(() => {
      this.showDeletionAlert(poll, 'success');
      if (app.current.matches(ComposePollPage) || app.current.matches(PollViewPage)) {
        m.route.set(app.route('fof.polls.list'));
      } else {
        PollListState.notifyDeleted(poll);
      }
    }).catch(() => this.showDeletionAlert(poll, 'error'));
  },
  /**
   * Show deletion alert of poll
   */
  showDeletionAlert(poll, type) {
    const message = {
      success: "fof-polls.forum.poll_controls.delete_success_message",
      error: "fof-polls.forum.poll_controls.delete_error_message"
    }[type];
    const content = app.translator.trans(message, {
      poll: poll
    });
    const alertId = app.alerts.show({
      type
    }, content);
    // Errors stay sticky so the user can read them; successes auto-dismiss.
    if (type === 'success') {
      setTimeout(() => app.alerts.dismiss(alertId), 10000);
    }
  },
  /**
   * Edit the poll.
   */
  editAction(poll) {
    m.route.set(app.route('fof.polls.composer', {
      id: poll.id()
    }));
  },
  async publishAction(poll) {
    try {
      await poll.publish();
      const alertId = app.alerts.show({
        type: 'success'
      }, app.translator.trans('fof-polls.forum.poll_controls.publish_success'));
      setTimeout(() => app.alerts.dismiss(alertId), 10000);
      m.redraw();
    } catch (e) {
      var _e$response;
      const detail = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail;
      app.alerts.show({
        type: 'error'
      }, detail != null ? detail : app.translator.trans('fof-polls.forum.poll_form.error'));
    }
  },
  async cancelScheduleAction(poll) {
    try {
      await poll.publish({
        scheduledFor: null
      });
      const alertId = app.alerts.show({
        type: 'success'
      }, app.translator.trans('fof-polls.forum.poll_controls.cancel_schedule_success'));
      setTimeout(() => app.alerts.dismiss(alertId), 10000);
      m.redraw();
    } catch (e) {
      var _e$response2;
      const detail = e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail;
      app.alerts.show({
        type: 'error'
      }, detail != null ? detail : app.translator.trans('fof-polls.forum.poll_form.error'));
    }
  },
  async unpublishAction(poll) {
    if (!confirm(app.translator.trans('fof-polls.forum.poll_controls.unpublish_confirmation'))) return;
    try {
      await poll.unpublish();
      const alertId = app.alerts.show({
        type: 'success'
      }, app.translator.trans('fof-polls.forum.poll_controls.unpublish_success'));
      setTimeout(() => app.alerts.dismiss(alertId), 10000);
      m.redraw();
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('fof-polls.forum.poll_controls.unpublish_error_has_votes'));
    }
  }
};